import React, {useState} from 'react';
import {useForm} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {jwtDecode} from "jwt-decode";

import styles from './RegularCustomer.module.scss';
import {regularCustomerValidator} from "../../validators/regularCustomer.validator";
import ModalCreatedOrder from "../ModalCreatedOrder/ModalCreatedOrder";
import {stepperInfoActions} from "../../store/slices/stepperInfoSlice";
import {MAIN_ROUTES} from "../../routing/main_routes";
import {userService} from "../../services/user.service";
import {userDetailsActions} from "../../store/slices/userDetailsSlice";
import {getHttpErrorMessageByStatus} from "../../constants/getHttpErrorMessageByStatus";
import {cleaningRequestsActions} from "../../store/slices/cleaningRequestsSlice";
import {cleaningRequestService} from "../../services/cleaningRequest.service";
import {qrCodeActions} from "../../store/slices/qrCodeSlice";
import {UIActions} from "../../store/slices/UISlice";
import {animateScroll as scroll} from "react-scroll";
import {buildCleaningAddress} from "../../utils/buildCleaningAddress";
import {urlsToFiles} from "../../utils/urlsToFiles";

const RegularCustomer = ({error, setError, setCurrentStep}) => {
    const navigate = useNavigate();

    const [isModalCreatedOrderOpen, setIsModalCreatedOrderOpen] = useState(false);

    const dispatch = useDispatch();

    const stepperInfo = useSelector(state => state.stepperInfo);

    const {
        service_package,
        countPanoramicWindows: pano_window_count,
        countOrdinaryWindows: reg_window_count,
        typeCleaning: cleaning_type,
        squareApartment: area,
        countRooms: room_count,
        countBathrooms: wc_count,
        request_price,
        promo_code,
        accessToApartment: present_at_home,
        locationAccessToApartment: present_at_home_desc,
        services,
        request_datetime,
        request_files,
        comment,
    } = stepperInfo;
    const {photos} = useSelector(state => state.qrCode);

    const turnReceivedImgUrlsToFiles = (photoUrls) => urlsToFiles(photoUrls);

    const {
        register,
        handleSubmit,
        formState: {errors, isValid},
    } = useForm({
        resolver: joiResolver(regularCustomerValidator),
        mode: 'all',
    });

    const handleCreateFormData = (photos, cleaning_request_data, qrCodePhotos) => {
        let formData = new FormData();

        if (photos?.length > 0) {
            for (const photo of photos) {
                formData.append('photos_data', photo);
            }
        }

        if (qrCodePhotos?.length > 0) {
            for (const photo of qrCodePhotos) {
                formData.append('photos_data', photo);
            }
        }

        formData.append('cleaning_request_related_data', JSON.stringify(cleaning_request_data));

        return formData;
    };

    const handleCreateOrder = async (data) => {
        try {
            dispatch(UIActions.setIsLoading(true));
            const {data: tokens} = await userService.login(data);
            const {cleaner_id, user_id, iat, exp} = jwtDecode(tokens.access);

            try {
                if (!cleaner_id) {
                    // Store tokens before any authenticated request so the
                    // axios interceptor can attach the Authorization header.
                    localStorage.setItem('isCleaner', 'false');
                    localStorage.setItem('token', JSON.stringify(tokens));
                    localStorage.setItem('expires', JSON.stringify({iat, exp}));

                    await dispatch(userDetailsActions.getById({id: user_id}));

                    //creating cleaning request
                    dispatch(cleaningRequestsActions.setStatus('loading'));

                    const servicesIds = services.map((service) => service.id);

                    const cleaning_request = {
                        ...buildCleaningAddress(stepperInfo),
                        cleaning_type: cleaning_type || 'Генеральне',
                        area,
                        services: servicesIds,
                        present_at_home,
                        present_at_home_desc: present_at_home_desc || null,
                        room_count: room_count || null,
                        wc_count: wc_count || null,
                        pano_window_count: pano_window_count || null,
                        reg_window_count: reg_window_count || null,
                        comment,
                        request_price,
                        promo_code: promo_code || null,
                        request_datetime,
                        service_package: service_package.id
                    };

                    const files = await turnReceivedImgUrlsToFiles(photos);
                    const formData = handleCreateFormData(request_files, cleaning_request, files);

                    await cleaningRequestService.create(formData);
                    navigate(MAIN_ROUTES.MAIN, {state: {id: user_id}});
                }
            } catch (error) {
                console.log(error, error?.response?.data);
                const errorMessage = getHttpErrorMessageByStatus(error);
                setError(errorMessage);
                dispatch(UIActions.setIsLoading(false));
                localStorage.clear(); // to log out the user when creating order is not successful
                setCurrentStep(1);
                localStorage.setItem('currentStep', JSON.stringify(1));

                setError(`На жаль, не вдалось створити замовлення. ${JSON.stringify(error?.response?.data)}`);
            } finally {
                dispatch(stepperInfoActions.clearAllData());
                dispatch(qrCodeActions.clearAllData());
                dispatch(UIActions.setIsLoading(false));
                dispatch(cleaningRequestsActions.setStatus('success'));
            }
        } catch (error) {
            console.error(error);
            setCurrentStep(1);
            setError(`На жаль, не вдалось увійти. ${JSON.stringify(error?.response?.data)}`);
            localStorage.setItem('currentStep', JSON.stringify(1));
            navigate(MAIN_ROUTES.CREATE_ORDER);
        } finally {
            dispatch(stepperInfoActions.clearAllData());
            dispatch(qrCodeActions.clearAllData());
            dispatch(UIActions.setIsLoading(false));
        }
    }

    const handleClearError = () => {
        if (error) {
            setError(null);
        }
    }

    const onError = (errors, e) => {
        scroll.scrollToTop();
    };

    return (
        <div className={styles.regularCustomer}>
            <form
                autoComplete='off'
                onChange={handleClearError}
                onSubmit={handleSubmit(handleCreateOrder, onError)}
                className={styles.regularCustomer_form}
                action="#"
            >
                <div className={styles.regularCustomer_form_block}>
                    <label>
                        <span>Email <sup>*</sup></span>
                        <input
                          style={{
                              borderColor: errors?.email && "#F04438"
                          }}
                            {...register('email')}
                            className={styles.input}
                            type="text"
                            autoComplete='new_email'
                        />
                    </label>
                    {errors?.email && <span className={styles.error}> {errors.email.message}</span>}
                </div>

                <div className={styles.regularCustomer_form_block}>
                    <label>
                        <span>Пароль <sup>*</sup></span>
                        <input
                          style={{
                              borderColor: errors?.password && "#F04438"
                          }}
                            {...register('password')}
                            className={styles.input}
                            type="password"
                            autoComplete='new_password'
                        />
                    </label>
                    {errors?.password && <span className={styles.error}> {errors.password.message}</span>}
                    {error && <span className={styles.error}> {error}</span>}
                </div>

                <button
                    type={'submit'}
                    // disabled={!isValid}
                    className={styles.button}
                >
                </button>
                {isModalCreatedOrderOpen && <ModalCreatedOrder/>}
            </form>
        </div>
    );
};

export default RegularCustomer;
